import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import QRCode from 'qrcode'
import {
  aiError,
  aiMoney,
  buyAiCredits,
  getAiHistory,
  getAiPurchase,
  getAiPurchases,
  getAiQuote,
  getAiState,
  sendAiMessage,
  type AiHistory,
  type AiOffer,
  type AiPurchase,
  type AiPurchases,
  type AiState,
} from './api'
import styles from './CampaignAi.module.css'

const date = (value: string) =>
  new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`).toLocaleString(
    'pt-BR',
    { dateStyle: 'short', timeStyle: 'short' }
  )
const statusLabel: Record<AiPurchase['status'], string> = {
  paid: 'Pago',
  pending: 'Aguardando PIX',
  creating: 'Gerando PIX',
  creation_uncertain: 'Em conciliação',
  expired: 'Expirado',
}

export function CampaignAiChat({
  campaignId,
  open,
  onClose,
  onPurchaseConfirmed,
}: {
  campaignId: number
  open: boolean
  onClose: () => void
  onPurchaseConfirmed?: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const bottom = useRef<HTMLDivElement>(null)
  const pixSection = useRef<HTMLElement>(null)
  const [data, setData] = useState<AiState | null>(null)
  const [history, setHistory] = useState<AiHistory | null>(null)
  const [purchases, setPurchases] = useState<AiPurchases | null>(null)
  const [tab, setTab] = useState<'chat' | 'credits'>('chat')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [buying, setBuying] = useState(false)
  const [purchase, setPurchase] = useState<AiPurchase | null>(null)
  const [qr, setQr] = useState('')
  const [historyLoading, setHistoryLoading] = useState(false)
  const [customCredits, setCustomCredits] = useState('10')
  const [customOffer, setCustomOffer] = useState<AiOffer | null>(null)
  const [customError, setCustomError] = useState('')
  const [quoting, setQuoting] = useState(false)
  const customQuantity = Number(customCredits)
  const customQuantityValid =
    Number.isInteger(customQuantity) && customQuantity >= 1 && customQuantity <= 100000
  const assistantEnabled = data?.enabled === true
  useEffect(() => {
    if (!open || tab !== 'credits' || !assistantEnabled || !customQuantityValid) return
    const controller = new AbortController()
    const timer = setTimeout(() => {
      setQuoting(true)
      setCustomError('')
      void getAiQuote(campaignId, customQuantity, controller.signal)
        .then((offer) => {
          if (!controller.signal.aborted) setCustomOffer(offer)
        })
        .catch((e: unknown) => {
          if (!controller.signal.aborted) {
            setCustomOffer(null)
            setCustomError(aiError(e))
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setQuoting(false)
        })
    }, 350)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [open, tab, assistantEnabled, customQuantityValid, customQuantity, campaignId])
  useEffect(() => {
    if (!open || tab !== 'credits' || !qr) return
    pixSection.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    pixSection.current?.focus({ preventScroll: true })
  }, [open, tab, qr])
  const sendLock = useRef(false)
  const buyLock = useRef(false)
  const attempt = useRef<{
    request_id: string
    message: string
    conversation_id: string | null
  } | null>(null)
  const purchaseAttempt = useRef<{ credits: number; id: string } | null>(null)
  const historySequence = useRef(0)
  const lastConfirmed = useRef<string | null>(null)
  const onConfirmed = useRef(onPurchaseConfirmed)
  useEffect(() => {
    onConfirmed.current = onPurchaseConfirmed
  }, [onPurchaseConfirmed])
  const refresh = useCallback(async () => {
    const next = await getAiState(campaignId)
    setData(next)
    setPurchases(next.purchases)
    return next
  }, [campaignId])

  useEffect(() => {
    if (!open) {
      if (dialog.current?.open) dialog.current.close()
      return
    }
    dialog.current?.showModal()
    const controller = new AbortController()
    setLoading(true)
    setError('')
    void getAiState(campaignId, controller.signal)
      .then(async (next) => {
        if (controller.signal.aborted) return
        setData(next)
        setPurchases(next.purchases)
        setPurchase(
          next.purchases.items.find((p) =>
            ['pending', 'creating', 'creation_uncertain'].includes(p.status)
          ) ?? null
        )
        if (next.conversations[0]) {
          const recent = await getAiHistory(
            campaignId,
            next.conversations[0].id,
            controller.signal
          )
          if (!controller.signal.aborted) setHistory(recent)
        } else setHistory(null)
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(aiError(e))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [open, campaignId])

  const pending = history?.turns.some((turn) => turn.status === 'pending') ?? false
  const activeConversationId = history?.id
  const activePurchaseId = purchase?.id
  const activePurchaseStatus = purchase?.status
  const awaitingNumbers =
    purchase?.status === 'paid' && purchase.numbers_assigned < purchase.numbers_quantity
  useEffect(() => {
    if (!open || !pending || !activeConversationId) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    async function poll() {
      try {
        const next = await getAiState(campaignId)
        const turns = await getAiHistory(campaignId, activeConversationId!)
        if (!cancelled) {
          setData(next)
          setHistory(turns)
        }
      } catch {
        /* Preserve the conversation; the next read reconciles interrupted replies. */
      }
      if (!cancelled) timer = setTimeout(() => void poll(), 5000)
    }
    timer = setTimeout(() => void poll(), 5000)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, pending, activeConversationId, campaignId])

  useEffect(() => {
    if (
      !open ||
      !activePurchaseId ||
      !activePurchaseStatus ||
      (!['pending', 'creating'].includes(activePurchaseStatus) && !awaitingNumbers)
    )
      return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const id = activePurchaseId
    async function poll() {
      try {
        const next = await getAiPurchase(campaignId, id)
        if (cancelled) return
        if (next.status === 'paid') {
          await refresh()
          if (cancelled) return
          const firstConfirmation = lastConfirmed.current !== id
          if (firstConfirmation) {
            lastConfirmed.current = id
            setError('')
            setNotice('Pagamento confirmado! Seus créditos já estão disponíveis.')
            setTab('chat')
          }
          setPurchase(next)
          if (firstConfirmation || next.numbers_assigned >= next.numbers_quantity) {
            onConfirmed.current?.()
          }
          if (next.numbers_assigned >= next.numbers_quantity) return
        } else setPurchase(next)
        if (!['pending', 'creating', 'paid'].includes(next.status)) return
      } catch {
        /* Keep the PIX available during transient network errors. */
      }
      if (!cancelled) timer = setTimeout(() => void poll(), 6000)
    }
    timer = setTimeout(() => void poll(), 4000)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, activePurchaseId, activePurchaseStatus, awaitingNumbers, campaignId, refresh])

  useEffect(() => {
    let cancelled = false
    setQr('')
    if (purchase?.pix_key)
      void QRCode.toDataURL(purchase.pix_key, { width: 220, margin: 2 })
        .then((url) => {
          if (!cancelled) setQr(url)
        })
        .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [purchase?.pix_key])
  useEffect(() => {
    bottom.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' })
  }, [history?.turns.length, sending, tab])

  async function chooseConversation(id: string) {
    if (sending) return
    const sequence = ++historySequence.current
    setHistoryLoading(true)
    setError('')
    try {
      const result = await getAiHistory(campaignId, id)
      if (sequence === historySequence.current) {
        setHistory(result)
        attempt.current = null
      }
    } catch (e) {
      if (sequence === historySequence.current) setError(aiError(e))
    } finally {
      if (sequence === historySequence.current) setHistoryLoading(false)
    }
  }
  async function send(event: FormEvent) {
    event.preventDefault()
    const text = message.trim()
    if (
      !text ||
      sendLock.current ||
      pending ||
      historyLoading ||
      !data?.enabled ||
      data.balance < 1
    )
      return
    sendLock.current = true
    setSending(true)
    setError('')
    setNotice('')
    const conversationId = history?.id ?? null
    if (
      attempt.current?.message !== text ||
      attempt.current?.conversation_id !== conversationId
    )
      attempt.current = {
        request_id: crypto.randomUUID(),
        message: text,
        conversation_id: conversationId,
      }
    try {
      const result = await sendAiMessage(campaignId, attempt.current)
      setHistory((current) => ({
        id: result.conversation_id,
        title: current?.title ?? text.slice(0, 70),
        turns: [
          ...(current?.turns.filter((t) => t.id !== result.turn.id) ?? []),
          result.turn,
        ],
      }))
      setMessage('')
      attempt.current = null
      if (result.turn.status === 'failed')
        setNotice(
          'A resposta não foi concluída. Seu crédito foi devolvido; você pode tentar novamente.'
        )
      await refresh()
    } catch (e) {
      setError(aiError(e))
    } finally {
      sendLock.current = false
      setSending(false)
    }
  }
  async function buy(offer: AiOffer) {
    if (buyLock.current) return
    buyLock.current = true
    setBuying(true)
    setError('')
    setNotice('')
    if (purchaseAttempt.current?.credits !== offer.credits)
      purchaseAttempt.current = { credits: offer.credits, id: crypto.randomUUID() }
    try {
      const result = await buyAiCredits(campaignId, offer, purchaseAttempt.current.id)
      setPurchase(result)
      purchaseAttempt.current = null
      await refresh()
    } catch (e) {
      setError(aiError(e))
      void refresh().catch(() => {})
    } finally {
      buyLock.current = false
      setBuying(false)
    }
  }
  async function purchasePage(page: number) {
    try {
      setPurchases(await getAiPurchases(campaignId, page))
    } catch (e) {
      setError(aiError(e))
    }
  }
  const hasPendingPurchase =
    purchase && ['pending', 'creating', 'creation_uncertain'].includes(purchase.status)
  return createPortal(
    <dialog
      ref={dialog}
      className={styles.dialog}
      aria-labelledby="campaign-ai-title"
      onCancel={onClose}
      onClose={onClose}
    >
      <header className={styles.chatHeader}>
        <span className={styles.robot}>
          <Icon icon="mdi:robot-happy-outline" width={30} />
        </span>
        <div>
          <span className={styles.eyebrow}>INTELIGÊNCIA ARTIFICIAL</span>
          <h2 id="campaign-ai-title">{data?.name ?? 'Assistente da campanha'}</h2>
        </div>
        <button
          className={styles.close}
          type="button"
          aria-label="Fechar chat"
          onClick={onClose}
        >
          <Icon icon="mdi:close" width={24} />
        </button>
      </header>
      <div className={styles.chatToolbar}>
        <div className={styles.tabs}>
          <button
            type="button"
            aria-pressed={tab === 'chat'}
            onClick={() => setTab('chat')}
          >
            <Icon icon="mdi:message-text-outline" /> Conversar
          </button>
          <button
            type="button"
            aria-pressed={tab === 'credits'}
            onClick={() => setTab('credits')}
          >
            <Icon icon="mdi:wallet-outline" /> Créditos e compras
          </button>
        </div>
        <strong className={styles.balance}>
          {data?.balance ?? '—'} <span>créditos</span>
        </strong>
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}{' '}
          {!data && (
            <button
              type="button"
              onClick={() =>
                void refresh()
                  .then(() => setError(''))
                  .catch((e) => setError(aiError(e)))
              }
            >
              Tentar novamente
            </button>
          )}
        </p>
      )}
      {notice && (
        <p role="status" className={styles.notice}>
          {notice}
        </p>
      )}
      {loading ? (
        <div className={styles.empty} role="status">
          Preparando seu espaço de conversa…
        </div>
      ) : (
        data && (
          <>
            {tab === 'chat' ? (
              <div className={styles.chatBody}>
                <div className={styles.historyBar}>
                  <label className={styles.srOnly} htmlFor="ai-conversation">
                    Histórico de conversas
                  </label>
                  <select
                    id="ai-conversation"
                    value={history?.id ?? ''}
                    disabled={sending || historyLoading}
                    onChange={(e) => {
                      if (e.target.value) void chooseConversation(e.target.value)
                    }}
                  >
                    <option value="">Nova conversa</option>
                    {data.conversations.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={styles.secondary}
                    disabled={sending || pending}
                    onClick={() => {
                      historySequence.current++
                      setHistoryLoading(false)
                      setHistory(null)
                      setMessage('')
                      attempt.current = null
                    }}
                  >
                    + Nova conversa
                  </button>
                </div>
                <div
                  className={styles.messages}
                  role="log"
                  aria-label="Mensagens da conversa"
                  aria-busy={sending || historyLoading}
                >
                  {(!history || history.turns.length === 0) && (
                    <div className={styles.empty}>
                      <span className={styles.largeRobot}>
                        <Icon icon="mdi:robot-happy-outline" width={54} />
                      </span>
                      <h3>Uma conversa. Muitas possibilidades.</h3>
                      <p>
                        {data.welcome_message ||
                          'Olá! Posso ajudar com dúvidas sobre esta campanha.'}
                      </p>
                      <div className={styles.suggestions}>
                        {[
                          'Qual é o objetivo desta campanha?',
                          'Como posso ajudar a entidade?',
                        ].map((text) => (
                          <button
                            type="button"
                            key={text}
                            onClick={() => setMessage(text)}
                          >
                            {text} <Icon icon="mdi:arrow-top-right" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {history?.turns.map((turn) => (
                    <div key={turn.id} className={styles.turn}>
                      <div className={styles.userMessage}>
                        <small>Você · {date(turn.created_at)}</small>
                        <p>{turn.message}</p>
                      </div>
                      <div className={styles.aiMessage}>
                        <small>
                          <Icon icon="mdi:robot-outline" /> {data.name}
                        </small>
                        {turn.status === 'completed' ? (
                          <p>{turn.answer}</p>
                        ) : (
                          <p className={styles.muted}>
                            {turn.status === 'pending'
                              ? 'Pensando na sua resposta…'
                              : 'Não foi possível responder. O crédito foi devolvido.'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <p className={styles.thinking} role="status">
                      <Icon icon="mdi:dots-horizontal" /> Preparando uma resposta…
                    </p>
                  )}
                  <div ref={bottom} />
                </div>
                {!data.enabled && (
                  <p className={styles.notice}>
                    O assistente está temporariamente indisponível. Seu saldo e histórico
                    estão preservados.
                  </p>
                )}
                {data.enabled && data.balance === 0 && !pending && (
                  <button
                    type="button"
                    className={styles.creditNudge}
                    onClick={() => setTab('credits')}
                  >
                    Adicione créditos para conversar <Icon icon="mdi:arrow-right" />
                  </button>
                )}
                <form className={styles.composer} onSubmit={(event) => void send(event)}>
                  <label className={styles.srOnly} htmlFor="ai-message">
                    Sua mensagem
                  </label>
                  <textarea
                    id="ai-message"
                    placeholder="O que você gostaria de saber?"
                    rows={2}
                    maxLength={4000}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={sending || pending || !data.enabled}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        !e.nativeEvent.isComposing
                      ) {
                        e.preventDefault()
                        e.currentTarget.form?.requestSubmit()
                      }
                    }}
                  />
                  <button
                    className={styles.send}
                    aria-label="Enviar mensagem"
                    disabled={
                      !message.trim() ||
                      sending ||
                      pending ||
                      historyLoading ||
                      !data.enabled ||
                      data.balance < 1
                    }
                  >
                    <Icon icon="mdi:arrow-up" width={24} />
                  </button>
                </form>
                <p className={styles.footnote}>
                  1 crédito por resposta concluída. A IA pode errar; confira o
                  regulamento. Histórico de {data.history_days} dias, com contexto das
                  mensagens recentes. Exibimos as últimas 50 conversas e até 100 respostas
                  por conversa. Ao enviar, seu texto é processado pela OpenAI. Evite dados
                  sensíveis.
                </p>
              </div>
            ) : (
              <div className={styles.shop}>
                <div className={styles.shopIntro}>
                  <span className={styles.eyebrow}>CRÉDITOS PARA CONVERSAR</span>
                  <h3>Mais créditos, novas conversas.</h3>
                  <p>
                    Escolha seu pacote e pague com PIX para continuar conversando com a
                    IA.
                  </p>
                </div>
                <div className={styles.stats}>
                  <div>
                    <strong>{data.balance}</strong>
                    <span>Créditos disponíveis</span>
                  </div>
                  <div>
                    <strong>{purchases?.paid_count ?? 0}</strong>
                    <span>Compras confirmadas</span>
                  </div>
                  <div>
                    <strong>{aiMoney(purchases?.total_paid_cents ?? 0)}</strong>
                    <span>Total em compras</span>
                  </div>
                </div>
                {data.sales_message && (
                  <p className={styles.notice}>{data.sales_message}</p>
                )}
                <div className={styles.offers}>
                  {data.offers.map((offer) => (
                    <article className={styles.offer} key={offer.credits}>
                      <span>
                        {offer.credits === 15 ? 'MAIS CONVERSAS' : 'PACOTE DE CRÉDITOS'}
                      </span>
                      <h4>
                        {offer.credits} <small>créditos</small>
                      </h4>
                      <strong>{aiMoney(offer.amount_cents)}</strong>
                      {offer.numbers_quantity > 0 && (
                        <p>
                          <Icon icon="mdi:ticket-confirmation-outline" />{' '}
                          {offer.numbers_quantity} números da campanha
                        </p>
                      )}
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={buying || Boolean(hasPendingPurchase)}
                        onClick={() => void buy(offer)}
                      >
                        {buying ? 'Preparando…' : 'Comprar com PIX'}
                      </button>
                    </article>
                  ))}
                </div>
                <form
                  className={styles.card}
                  onSubmit={(event) => {
                    event.preventDefault()
                    if (
                      customQuantityValid &&
                      customOffer?.credits === customQuantity &&
                      !quoting &&
                      !hasPendingPurchase
                    )
                      void buy(customOffer)
                  }}
                >
                  <h3>Escolha quantos créditos comprar</h3>
                  <div className={styles.customPurchase}>
                    <label>
                      Quantidade de créditos
                      <input
                        type="number"
                        min={1}
                        max={100000}
                        step={1}
                        required
                        value={customCredits}
                        disabled={buying || Boolean(hasPendingPurchase)}
                        onChange={(event) => {
                          setCustomCredits(event.target.value)
                          setCustomOffer(null)
                          setCustomError('')
                          setQuoting(false)
                        }}
                      />
                    </label>
                    <div aria-live="polite">
                      <small>{aiMoney(data.credit_price_cents)} por crédito</small>
                      <strong>
                        {customOffer?.credits === customQuantity && customQuantityValid
                          ? aiMoney(customOffer.amount_cents)
                          : quoting
                            ? 'Calculando…'
                            : '—'}
                      </strong>
                      {customOffer?.credits === customQuantity &&
                        customOffer.numbers_quantity > 0 && (
                          <p>{customOffer.numbers_quantity} números da campanha</p>
                        )}
                    </div>
                    <button
                      className={styles.primary}
                      disabled={
                        !assistantEnabled ||
                        !customQuantityValid ||
                        customOffer?.credits !== customQuantity ||
                        quoting ||
                        buying ||
                        Boolean(hasPendingPurchase)
                      }
                    >
                      {buying ? 'Preparando…' : 'Comprar quantidade escolhida'}
                    </button>
                  </div>
                  {customError && (
                    <p className={styles.error} role="alert">
                      {customError}
                    </p>
                  )}
                  {!customQuantityValid && (
                    <p>Informe uma quantidade inteira a partir de 1 crédito.</p>
                  )}
                </form>
                <p className={styles.footnote}>
                  Créditos exclusivos desta campanha, sem vencimento.
                </p>
                {purchase && (
                  <section
                    className={styles.pix}
                    ref={pixSection}
                    tabIndex={-1}
                    aria-label="Pagamento PIX dos créditos"
                  >
                    <div>
                      <h3>
                        {purchase.status === 'paid'
                          ? 'Pagamento confirmado'
                          : 'Sua compra de créditos'}
                      </h3>
                      <p>
                        {purchase.credits} créditos · {aiMoney(purchase.amount_cents)} ·{' '}
                        {statusLabel[purchase.status]}
                      </p>
                      {purchase.status === 'pending' && (
                        <p>
                          Válido até {date(purchase.expires_at)}. A confirmação é
                          automática.
                        </p>
                      )}
                      {purchase.status === 'creation_uncertain' && (
                        <p>
                          O gateway ainda não confirmou a criação. Não gere outro
                          pagamento. Procure a entidade com a referência {purchase.id}.
                        </p>
                      )}
                      {purchase.status === 'expired' && (
                        <p>
                          Este código expirou. Escolha um pacote para iniciar uma nova
                          compra.
                        </p>
                      )}
                      {purchase.status === 'paid' && (
                        <p>
                          {purchase.numbers_assigned} números já disponíveis de{' '}
                          {purchase.numbers_quantity}. Acompanhe a entrega na aba Números
                          do perfil.
                        </p>
                      )}
                    </div>
                    {qr && (
                      <img
                        src={qr}
                        alt="QR Code PIX para comprar créditos de IA"
                        width={220}
                        height={220}
                      />
                    )}
                    {purchase.pix_key && (
                      <div className={styles.pixCode}>
                        <label>
                          Código PIX copia e cola
                          <textarea
                            readOnly
                            value={purchase.pix_key}
                            rows={3}
                            onFocus={(e) => e.target.select()}
                          />
                        </label>
                        <button
                          type="button"
                          className={styles.secondary}
                          onClick={() =>
                            void navigator.clipboard
                              .writeText(purchase.pix_key!)
                              .then(() => setNotice('Código PIX copiado.'))
                              .catch(() =>
                                setNotice('Selecione e copie o código PIX acima.')
                              )
                          }
                        >
                          Copiar código PIX
                        </button>
                      </div>
                    )}
                  </section>
                )}
                <section className={styles.card}>
                  <h3>Seu histórico de compras</h3>
                  {purchases?.items.length ? (
                    <div className={styles.purchaseList}>
                      {purchases.items.map((p) => (
                        <div className={styles.purchaseRow} key={p.id}>
                          <div>
                            <strong>
                              {p.credits} créditos · {aiMoney(p.amount_cents)}
                            </strong>
                            <small>
                              {date(p.created_at)}
                              {p.credited_at ? ` · Pago em ${date(p.credited_at)}` : ''}
                            </small>
                            <small>
                              {p.numbers_assigned} / {p.numbers_quantity} números
                              entregues
                            </small>
                          </div>
                          <button
                            type="button"
                            className={styles.secondary}
                            onClick={() => setPurchase(p)}
                          >
                            {statusLabel[p.status]}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>Suas compras aparecerão aqui após escolher um pacote.</p>
                  )}
                  {purchases && purchases.last_page > 1 && (
                    <div className={styles.pagination}>
                      <button
                        type="button"
                        disabled={purchases.page <= 1}
                        onClick={() => void purchasePage(purchases.page - 1)}
                      >
                        Anterior
                      </button>
                      <span>
                        {purchases.page} de {purchases.last_page}
                      </span>
                      <button
                        type="button"
                        disabled={purchases.page >= purchases.last_page}
                        onClick={() => void purchasePage(purchases.page + 1)}
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </section>
              </div>
            )}
          </>
        )
      )}
    </dialog>,
    document.body
  )
}
